(ns admin.components.composite.moment-editor.core
  (:require
   [applied-science.js-interop :as j]
   [reagent.core :as reagent]
   [re-frame.core :as re-frame]
   [reagent-form.utils :as rfu]

   [admin.components.composite.moment-editor.edit :as moment-edit]
   [admin.components.composite.moment-editor.moments-list :as moments-list]
   [admin.components.composite.moment-editor.pdf-plo :as pdf-plo]
   [admin.components.composite.moment-editor.video-plo :as video-plo]
   [admin.events.moments :as >moments]
   [admin.utils :as au]

   [kyber.lib.kyber-db :as kyber-db]
   [kyber.styles.core :refer [gs styles->classes]]
   [kyber.lib.pdftron.core :as pdftron]
   [kyber.utils :as ku]))

(def moments-width 540)

(def classes
  (styles->classes
   {:container
    {:display :flex
     :flex "1 1 auto"
     :position :relative}

    :plo-container
    {:background (gs [:colors :primary-gray-100])
     :width (str "calc(100% - " moments-width "px)")
     :display :flex
     :flex-direction :column}

    :moments-container
    {:width (str moments-width "px")
     :overflow-y :auto
     :position :absolute
     :background-color (gs [:colors :primary-gray-50])
     :box-shadow "inset 0 0 15px 4px rgba(0,0,0,0.15)"
     :right 0
     :top 0
     :bottom 0}}))

(defn- unsaved-changes-notification [on-confirm]
  (re-frame/dispatch
   [:notifier/create-alert
    {:title "There are unsaved changes."
     :message (str "Are you sure you want to ignore "
                   "your changes?")
     :confirm-text "Ignore Changes"
     :confirm-action on-confirm
     :deny-text "Continue Editing"}]))

;; TODO Update this to use domestic
(defmulti dispatcher identity)

(defmethod dispatcher
  :highlight-selected-pdf-moment
  [_ editor-state _ moment]
  (let [{:keys [moment-list-ref!]
         viewer-key :player-key}
        @editor-state

        {xfdf-id :xfdf-id
         moment-uuid :uuid}
        moment

        card-ref!
        (some-> @moment-list-ref!
                (j/call :querySelector (str "#moment_card_" moment-uuid)))

        {:keys [apis]}
        (pdftron/get-pdftron-viewer viewer-key)

        annotation-manager
        (:annotation-manager apis)

        annotation
        (j/call annotation-manager :getAnnotationById xfdf-id)]
    (when (some? card-ref!)
      (doseq [moment-ref! (j/call @moment-list-ref! :querySelectorAll "li[style*='border']")]
        (j/assoc! moment-ref! :style ""))
      (j/call annotation-manager :deselectAllAnnotations)
      (j/call annotation-manager :selectAnnotation annotation) ;;Note: This causes unexpected scrolling
      (ku/scroll-to @moment-list-ref!
                    {:top (- (j/get card-ref! :offsetTop) 30)})
      (j/assoc! card-ref! :style (str "border: 3px solid " (gs [:colors :primary-lite-500])))
      (re-frame/dispatch [:pdftron-viewer/go-to-annotation {:xfdf-id xfdf-id
                                                            :viewer-key viewer-key}]))))

(defmethod dispatcher
  :highlight-selected-video-moment
  [_ editor-state _ moment]
  (let [{:keys [moment-list-ref!]}
        @editor-state

        {moment-uuid :uuid}
        moment

        card-ref!
        (some-> @moment-list-ref!
                (j/call :querySelector (str "#moment_card_" moment-uuid)))]
    (when (some? card-ref!)
      (doseq [moment-ref! (j/call @moment-list-ref! :querySelectorAll "li[style*='border']")]
        (j/assoc! moment-ref! :style ""))
      (ku/scroll-to @moment-list-ref!
                    {:top (- (j/get card-ref! :offsetTop) 30)})
      (j/assoc! card-ref! :style (str "border: 3px solid " (gs [:colors :primary-lite-500]))))))

(defmethod dispatcher
  :enter-moment-edit-view
  [_ editor-state _ panel-key]
  (swap! editor-state assoc
         :active-panel panel-key
         :editing? true))

(defmethod dispatcher
  :exit-moment-edit-view
  [_ editor-state _ {deleted? :deleted?}]
  (let [{:keys [dirty?]} @editor-state
        action #(do
                  (when deleted?
                    (re-frame/dispatch [::>moments/unset-active-moment-uuid]))
                  (swap! editor-state assoc
                         :dirty? false
                         :active-panel nil
                         :new-question nil
                         :temp-moment-form-state (atom nil)
                         :moment-location-invalid false
                         :editing? false
                         :question-text nil
                         :answer-options nil
                         :show-results nil
                         :add-copy nil
                         :free-text-response nil
                         :question-deleted? false)
                  (if (false? deleted?)
                    (swap! editor-state assoc
                           :highlight-active-moment? true)
                    (swap! editor-state assoc
                           :highlight-active-moment? false)))]
    (if (true? dirty?)
      (unsaved-changes-notification #(action))
      (action))))

(defmethod dispatcher
  :set-active-panel
  [_ editor-state _ panel-key]
  (let [{:keys [dirty?]} @editor-state
        action #(swap! editor-state assoc
                       :active-panel panel-key
                       ;; Clear temp moment when navigating to new panel
                       :temp-moment-form-state (atom nil)
                       :dirty? false)]
    (if (true? dirty?)
      (unsaved-changes-notification
       #(action))
      (action))))

(defmethod dispatcher
  :unset-active-panel
  [_ editor-state _]
  (swap! editor-state assoc
         :active-panel nil
         :temp-moment-form-state (atom nil)))

(defmethod dispatcher
  :set-active-tool
  [_ editor-state _ active-tool]
  (swap! editor-state assoc :active-tool active-tool))

(defmethod dispatcher
  :unset-active-tool
  [_ editor-state _]
  (swap! editor-state assoc :active-tool nil))

(defmethod dispatcher
  :on-annotation-selected
  [_ editor-state _ annotation is-deselected?]
  (let [{:keys [editing? moment-list-ref! activity-uuid]} @editor-state
        {moments :activity/moments}
        @(re-frame/subscribe
          [::kyber-db/get-record
           {:model :activity
            :ident activity-uuid
            :query [:activity/moments]}])]
    (when (and (false? editing?)
               (some? annotation))
      (let [annotation-id
            (pdftron/get-annotation-id annotation)

            {moment-uuid :moment/uuid}
            (->> moments
                 (filter #(= annotation-id (:moment/xfdf-id %)))
                 first)

            card-ref!
            (some-> @moment-list-ref!
                    (j/call :querySelector (str "#moment_card_" moment-uuid)))]

        (when (and (some? card-ref!)
                   (not is-deselected?))
          (j/assoc! card-ref! :style (str "border: 3px solid " (gs [:colors :primary-lite-500])))
          (ku/scroll-to @moment-list-ref!
                        {:top (- (j/get card-ref! :offsetTop) 30)}))))

    (when (and (false? editing?)
               (or (nil? annotation) (undefined? annotation))
               is-deselected?)
      (let [card-ref!
            (some-> @moment-list-ref!
                    (j/call :querySelector "li[style*='border']"))]
        (j/assoc! card-ref! :style "")))))

(defmethod dispatcher
  :set-dirty
  [_ editor-state _ is-dirty]
  (swap! editor-state assoc :dirty? is-dirty))

(defmethod dispatcher
  :set-new-question
  [_ editor-state _ new-question]
  (swap! editor-state assoc :new-question new-question))

(defmethod dispatcher
  :unset-new-question
  [_ editor-state _]
  (swap! editor-state assoc :new-question nil))

(defmethod dispatcher
  :set-temp-moment-form-state
  [_ editor-state _ temp-moment-form-state]
  (swap! editor-state assoc :temp-moment-form-state temp-moment-form-state))

(defmethod dispatcher
  :set-form-fields
  [_ editor-state _ {:keys [question-text answer-options show-results add-copy free-text-response]}]
  (swap! editor-state assoc
         :question-text question-text
         :answer-options answer-options
         :add-copy add-copy)
  (when (some? free-text-response)
    (swap! editor-state assoc :free-text-response free-text-response))
  (when (some? show-results)
    (swap! editor-state assoc :show-results show-results)))

(defmethod dispatcher
  :set-form-fields-free-response
  [_ editor-state _ {:keys [question-text add-copy]}]
  (swap! editor-state assoc
         :question-text question-text
         :add-copy add-copy))

(defmethod dispatcher
  :update-question-deleted?
  [_ editor-state _ boolean]
  (swap! editor-state assoc :question-deleted? boolean))

(defmethod dispatcher
  :update-temp-moment
  [_ editor-state _ {:keys [marker-props]}]
  (let [form-state (:temp-moment-form-state @editor-state)]
    (doseq [[field-key value] marker-props]
      (rfu/update-field-value! form-state field-key value))))

(defmethod dispatcher
  :set-moment-location-invalid
  [_ editor-state moments {:keys [uuid marker-props]}]
  (swap! editor-state assoc
         :moment-location-invalid
         (not (au/valid-video-moment-location?
               (-> marker-props
                   (select-keys [:display-at :hide-at])
                   (assoc :moments moments
                          :uuid uuid))))))

(defmethod dispatcher :default [method _]
  (js/console.error (str "local dispatcher handler " method " not defined.")))

(defn render [{:keys [activity curriculum]}]
  (let [player-key
        :activity-moment-tool

        {curriculum-uuid :curriculum/uuid}
        @curriculum

        {activity-uuid :activity/uuid
         :activity/keys
         [primary-learning-object plo-type]}
        @activity

        video-activity?
        (ku/video-activity? @activity)

        pdf-activity?
        (ku/pdf-activity? @activity)

        moment-list-ref!
        (atom nil)

        timeline-state
        (reagent/atom {:adding-moment-at nil})

        editor-state
        (reagent/atom {:active-tool nil
                       :active-panel nil
                       :activity-uuid activity-uuid
                       :curriculum-uuid curriculum-uuid
                       :dirty? false
                       :duration (:resource/duration primary-learning-object)
                       :editing? false
                       :moment-location-invalid false
                       :moment-list-ref! moment-list-ref!
                       :new-question nil
                       :pdf-activity? (ku/pdf-activity? @activity)
                       :plo-type plo-type
                       :player-key player-key
                       :plo-uuid (:resource/uuid primary-learning-object)
                       :temp-moment-form-state (atom nil)
                       :video-activity? (ku/video-activity? @activity)})

        ]
    (fn [_]
      (let [{:activity/keys [moments _valid-tags]}
            @activity

            ;; TODO: Used as a predicate for `:show-visibility-panel?`
            ;; active-activity-tags? 
            ;; (->> valid-tags
            ;;      (filterv #(seq (:content-provider.tag-group/values %)))
            ;;      seq)

            local-dispatcher
            #(apply dispatcher (first %) editor-state moments (rest %))]
        [:div {:class (:container classes)}
         [:div {:class (:plo-container classes)}
          (cond
            video-activity?
            [video-plo/render {:activity activity
                               :player-key player-key
                               :editor-state editor-state
                               :dispatch-local local-dispatcher
                               :timeline-state timeline-state}]

            pdf-activity?
            [pdf-plo/render {:activity activity
                             :player-key player-key
                             :editor-state editor-state
                             :dispatch-local local-dispatcher
                             :event-bus {:on-annotation-selected [:on-annotation-selected]}}])]

         [:div {:class (:moments-container classes)}
          (if (true? (:editing? @editor-state))
            [moment-edit/render {:dispatch-local local-dispatcher
                                 :editor-state editor-state
                                 :show-visibility-panel? false}]
            [moments-list/render
             (cond-> {:activity activity
                      :dispatch-local local-dispatcher
                      :editor-state editor-state}
               video-activity?
               (assoc :timeline-state timeline-state))])]]))))
